import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import Tooltip from 'components/shared/Tooltip';
import { SupplementalEpisodeInformation } from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import Profile from 'models/Profile';
import { colors } from 'styles/theme/colors';
import LinkIcon from 'svg/LinkIcon';

import DraggablePatientCard from './PortfolioCard';
import SharedRecordSelectionModal from './SharedRecordSelectionModal';

type CardData = {
  locationEpisode: LocationEpisode;
  info: SupplementalEpisodeInformation;
};

type StackedPatientCardsProps = {
  cards: CardData[];
  profile: Profile;
  invalidateColumn: () => void;
};

function SharedRecordIndicator() {
  return (
    <Tooltip text='Patient shared between multiple records in Olio' position='left'>
      <LinkIndicator>
        <LinkIcon width={8} height={8} color={colors.black50} />
      </LinkIndicator>
    </Tooltip>
  );
}

const CARD_GAP = 10;
const COLLAPSED_OFFSET = 6;
const COLLAPSED_SCALE_STEP = 0.03;
const COLLAPSED_OPACITY_STEP = 0.2;

export default function StackedPatientCards({ cards, profile, invalidateColumn }: StackedPatientCardsProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [modalRecords, setModalRecords] = useState<LocationEpisode[] | null>(null);
  const primaryRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(0);

  const allRecords = [cards[0].locationEpisode, ...cards[0].locationEpisode.sharedRecords];
  const hasHiddenRecords = allRecords.length > cards.length;
  const openModal = hasHiddenRecords ? () => setModalRecords(allRecords) : undefined;

  const measureRef = useCallback((node: HTMLDivElement | null) => {
    primaryRef.current = node;
    if (node) setCardHeight(node.getBoundingClientRect().height);
  }, []);

  if (cards.length === 1) {
    const le = cards[0].locationEpisode;
    const hasSharedRecords = le.sharedRecords.length > 0;

    return (
      <>
        {hasSharedRecords ? (
          <SingleLinkedCard>
            <SharedRecordIndicator />
            <DraggablePatientCard
              locationEpisode={le}
              info={cards[0].info}
              profile={profile}
              invalidateColumn={invalidateColumn}
              onCardClick={openModal}
            />
          </SingleLinkedCard>
        ) : (
          <DraggablePatientCard
            locationEpisode={le}
            info={cards[0].info}
            profile={profile}
            invalidateColumn={invalidateColumn}
          />
        )}
        {modalRecords && (
          <SharedRecordSelectionModal records={modalRecords} profile={profile} onClose={() => setModalRecords(null)} />
        )}
      </>
    );
  }

  const stackedCards = cards.slice(1);
  const expandOffset = cardHeight + CARD_GAP;

  return (
    <>
      <StackContainer
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        $expanded={isHovered}
        $expandedPadding={isHovered ? stackedCards.length * expandOffset : stackedCards.length * COLLAPSED_OFFSET}
        $lineHeight={stackedCards.length * expandOffset}
        $cardHeight={cardHeight}>
        {stackedCards.map((card, i) => (
          <StackedLayer
            key={card.locationEpisode.id}
            $index={i}
            $total={stackedCards.length}
            $expanded={isHovered}
            $expandOffset={expandOffset}>
            <SharedRecordIndicator />
            <DraggablePatientCard
              locationEpisode={card.locationEpisode}
              info={card.info}
              profile={profile}
              invalidateColumn={invalidateColumn}
              onCardClick={openModal}
            />
          </StackedLayer>
        ))}

        <PrimaryLayer ref={measureRef} $stackCount={stackedCards.length}>
          <SharedRecordIndicator />
          <DraggablePatientCard
            locationEpisode={cards[0].locationEpisode}
            info={cards[0].info}
            profile={profile}
            invalidateColumn={invalidateColumn}
            onCardClick={openModal}
          />
        </PrimaryLayer>
      </StackContainer>
      {modalRecords && (
        <SharedRecordSelectionModal records={modalRecords} profile={profile} onClose={() => setModalRecords(null)} />
      )}
    </>
  );
}

const StackContainer = styled.div<{
  $expandedPadding: number;
  $expanded: boolean;
  $lineHeight: number;
  $cardHeight: number;
}>`
  position: relative;
  cursor: pointer;
  padding-bottom: ${({ $expandedPadding }) => $expandedPadding}px;
  transition: padding-bottom 300ms ease-out;

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: ${({ $cardHeight }) => $cardHeight / 2}px;
    height: ${({ $expanded, $lineHeight }) => ($expanded ? `${$lineHeight}px` : '0px')};
    width: 0;
    border-left: 2px dotted var(--black-25);
    z-index: 0;
    transition: height 300ms ease-out;
  }
`;

const PrimaryLayer = styled.div<{ $stackCount: number }>`
  position: relative;
  z-index: ${({ $stackCount }) => $stackCount + 1};
`;

const StackedLayer = styled.div<{ $index: number; $total: number; $expanded: boolean; $expandOffset: number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ $index, $total }) => $total - $index};
  transition: all 300ms ease-out;
  transform: ${({ $expanded, $index, $expandOffset }) =>
    $expanded
      ? `translateY(${($index + 1) * $expandOffset}px)`
      : `translateY(${($index + 1) * COLLAPSED_OFFSET}px) scale(${1 - ($index + 1) * COLLAPSED_SCALE_STEP})`};
  opacity: ${({ $expanded, $index }) => ($expanded ? 1 : 1 - ($index + 1) * COLLAPSED_OPACITY_STEP)};
`;

const SingleLinkedCard = styled.div`
  position: relative;
`;

const LinkIndicator = styled.div`
  position: absolute;
  left: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${colors.white};
  border: 1px solid ${colors.gray};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;
